/** The fields project resolution needs, so this stays testable without the full Tenant shape. */
export interface ProjectRef {
	id: string
	org_id?: string
}

/**
 * Which project the switcher should treat as selected.
 *
 * `orgTenants` is the org-filtered list, which drops the root project and everything in other orgs
 * (see useProjects). So it alone cannot decide whether `currentTenant` is real — `tenantList` is
 * consulted too, but only for a project in the same org, or the switcher would show a project the
 * user has since navigated away from.
 */
export function resolveEffectiveProjectId({
	routeTenantId,
	currentTenant,
	orgTenants,
	tenantList,
	orgId,
	defaultProjectId,
}: {
	routeTenantId?: string | null
	currentTenant?: string | null
	orgTenants: ProjectRef[]
	tenantList?: ProjectRef[] | null
	orgId?: string | null
	defaultProjectId?: string | null
}): string | null {
	// The route id is authoritative — that project is on screen.
	if (routeTenantId) {
		return routeTenantId
	}
	if (currentTenant && isProjectInCurrentOrg({ projectId: currentTenant, orgTenants, tenantList, orgId })) {
		return currentTenant
	}
	// The pin outlives the project it points at, and an id nothing can name renders as "Select Project".
	if (defaultProjectId && isProjectInCurrentOrg({ projectId: defaultProjectId, orgTenants, tenantList, orgId })) {
		return defaultProjectId
	}
	return orgTenants[0]?.id || null
}

/** With no org in context there is no filter to contradict `tenantList`, so membership alone counts. */
function isProjectInCurrentOrg({
	projectId,
	orgTenants,
	tenantList,
	orgId,
}: {
	projectId: string
	orgTenants: ProjectRef[]
	tenantList?: ProjectRef[] | null
	orgId?: string | null
}): boolean {
	if (orgTenants.some(t => t.id === projectId)) {
		return true
	}
	return tenantList?.some(t => t.id === projectId && (!orgId || t.org_id === orgId)) ?? false
}
